import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconTableFilter } from './icons';

function isFilterActive(filter) {
  if (!filter) return false;
  if (filter.type === 'text') return String(filter.value || '').trim() !== '';
  return filter.value !== undefined && filter.value !== null && filter.value !== '';
}

function SortAscIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path d="M4 5.5h12v1.5H4V5.5Zm2 4h8v1.5H6V9.5Zm2 4h4v1.5H8V13.5Z" />
    </svg>
  );
}

function SortDescIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path d="M8 5.5h4v1.5H8V5.5Zm-2 4h8v1.5H6V9.5Zm-2 4h12v1.5H4V13.5Z" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="h-5 w-5 shrink-0 text-[#5A5A5A]" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M5 7.5 10 12.5 15 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Column funnel. Sort columns get A–Z / Z–A. Filter columns get one Equals
 * value. Nothing is applied until Submit; Cancel and dismiss leave the table
 * as it was. And/Or and a second condition are omitted because the list APIs
 * accept a single value.
 */
export function ColumnHeaderFilter({
  header,
  mode = 'sort',
  filter,
  sortDir = '',
  open,
  onOpenChange,
  onApplySort,
}) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, caret: 16 });
  const [draftDir, setDraftDir] = useState('asc');
  const [draftValue, setDraftValue] = useState('');
  const active = mode === 'filter' ? isFilterActive(filter) : sortDir === 'asc' || sortDir === 'desc';
  const label = mode === 'filter' ? filter?.ariaLabel || `Filter ${header}` : `Sort ${header}`;

  function openPanel() {
    setDraftDir(sortDir === 'desc' ? 'desc' : 'asc');
    setDraftValue(filter?.value == null ? '' : String(filter.value));
    onOpenChange(true);
  }

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current.offsetWidth;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
    const caret = Math.min(
      menuWidth - 20,
      Math.max(12, rect.left + rect.width / 2 - left - 8)
    );
    setPos({ top: rect.bottom + 12, left, caret });
  }, [open, mode]);

  useEffect(() => {
    if (!open) return undefined;

    function onKey(event) {
      if (event.key === 'Escape') onOpenChange(false);
    }
    function onPointer(event) {
      const target = event.target;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onOpenChange(false);
    }
    function onViewport() {
      onOpenChange(false);
    }

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('scroll', onViewport, true);
    window.addEventListener('resize', onViewport);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('scroll', onViewport, true);
      window.removeEventListener('resize', onViewport);
    };
  }, [open, onOpenChange]);

  if (mode === 'filter' && (!filter || typeof filter.onChange !== 'function')) return null;

  function submit() {
    if (mode === 'sort') {
      onApplySort?.(draftDir === 'desc' ? 'desc' : 'asc');
    } else {
      const current = filter.value == null ? '' : String(filter.value);
      if (draftValue !== current) filter.onChange(draftValue);
    }
    onOpenChange(false);
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          if (open) onOpenChange(false);
          else openPanel();
        }}
        className="relative inline-flex shrink-0 rounded-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        <IconTableFilter className={`h-[18px] w-[18px] ${active ? 'opacity-100' : 'opacity-80'}`} />
        {active ? (
          <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-[#5CE1E6]" aria-hidden />
        ) : null}
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="dialog"
              aria-label="Custom Filter"
              className="fixed z-50 w-max max-w-[calc(100vw-16px)] rounded-2xl border border-[#EFEFEF] bg-[#F8F8F8] px-6 py-4 shadow-[0_4px_35px_rgba(0,0,0,0.15)]"
              style={{ top: pos.top, left: pos.left }}
            >
              <span
                className="absolute h-4 w-4 rotate-45 border-l border-t border-[#EFEFEF] bg-[#F8F8F8]"
                style={{ top: -8, left: pos.caret }}
                aria-hidden
              />
              <p className="text-[18px] font-bold leading-[22px] text-[#212121]">Custom Filter</p>
              {mode === 'sort' ? (
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3" role="radiogroup" aria-label={`Sort ${header}`}>
                  <label className="inline-flex cursor-pointer items-center gap-2 text-[#212121]">
                    <input
                      type="radio"
                      name={`sort-${header}`}
                      className="sr-only"
                      checked={draftDir !== 'desc'}
                      onChange={() => setDraftDir('asc')}
                    />
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                        draftDir !== 'desc' ? 'border-[#003A70]' : 'border-[#D8D4D4]'
                      }`}
                      aria-hidden
                    >
                      {draftDir !== 'desc' ? <span className="h-2.5 w-2.5 rounded-full bg-[#003A70]" /> : null}
                    </span>
                    <span className={`text-sm ${draftDir !== 'desc' ? 'font-bold' : 'font-normal text-[#5A5A5A]'}`}>
                      Sort By A to Z
                    </span>
                    <SortAscIcon />
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name={`sort-${header}`}
                      className="sr-only"
                      checked={draftDir === 'desc'}
                      onChange={() => setDraftDir('desc')}
                    />
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                        draftDir === 'desc' ? 'border-[#003A70]' : 'border-[#D8D4D4]'
                      }`}
                      aria-hidden
                    >
                      {draftDir === 'desc' ? <span className="h-2.5 w-2.5 rounded-full bg-[#003A70]" /> : null}
                    </span>
                    <span className={`text-sm ${draftDir === 'desc' ? 'font-bold text-[#212121]' : 'font-normal text-[#5A5A5A]'}`}>
                      Sort By Z to A
                    </span>
                    <SortDescIcon />
                  </label>
                </div>
              ) : (
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <div className="flex h-[50px] min-w-[140px] items-center justify-between rounded-[14px] border border-[#D8D4D4] bg-white px-4 text-sm font-medium text-[#1F2933]">
                    Equals
                    <ChevronIcon />
                  </div>
                  {filter.type === 'text' ? (
                    <input
                      aria-label={label}
                      value={draftValue}
                      onChange={(event) => setDraftValue(event.target.value)}
                      placeholder={filter.placeholder || 'Select'}
                      className="h-[50px] min-w-[180px] flex-1 rounded-[14px] border border-[#D8D4D4] bg-white px-4 text-sm text-[#1F2933] placeholder:text-[#5A5A5A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/30"
                    />
                  ) : (
                    <div className="relative min-w-[180px] flex-1">
                      <select
                        aria-label={label}
                        value={draftValue}
                        onChange={(event) => setDraftValue(event.target.value)}
                        className="h-[50px] w-full appearance-none rounded-[14px] border border-[#D8D4D4] bg-white px-4 pr-10 text-sm font-medium text-[#1F2933] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/30"
                      >
                        {(filter.options || []).map((option) => {
                          const optionValue = option.value == null ? '' : String(option.value);
                          return (
                            <option key={optionValue === '' ? 'all' : optionValue} value={optionValue}>
                              {option.label}
                            </option>
                          );
                        })}
                      </select>
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                        <ChevronIcon />
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-6 flex items-center justify-end gap-4">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="rounded-lg border border-[#003A70] bg-[#F4F7FA] px-4 py-3 text-sm font-semibold text-[#003A70] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  className="rounded-lg bg-[#003A70] px-4 py-3 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/40"
                >
                  Submit
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
